// Alt Surface Builder methodology defaults (IM-2487).
//
// The generated-token surface methodology is parameterised by a rolling-std
// window and two EWMA half-lives. These are the generation-spec defaults; the
// screen seeds its live controls from here so the default is defined once, not
// as a component literal (IM-2486 follow-up).
export interface AltSurfaceParams {
  // Short-leg rolling-std lookback, in daily observations (pairs with hlShort).
  windowSize: number;
  // Short EWMA half-life, in days.
  hlShort: number;
  // Long-leg rolling-std lookback, in daily observations (pairs with hlLong).
  // Optional: defaults to windowSize (the reference, where both legs share one
  // window). Set it to give the long ratio its own, typically longer, window.
  windowLong?: number;
  // Long EWMA half-life, in days.
  hlLong: number;
}

export const ALT_SURFACE_DEFAULT_PARAMS: AltSurfaceParams = {
  windowSize: 7,
  hlShort: 3,
  windowLong: 7,
  hlLong: 90,
};

// Slider bounds for the live controls. Integer steps only. The half-life ranges
// overlap at 30 deliberately — the guard in applyAltSurfaceParam keeps the two
// half-lives from meeting there (equal half-lives collapse the short/long blend
// to a flat scale).
export interface AltSurfaceParamRange {
  min: number;
  max: number;
  step: number;
}

export const ALT_SURFACE_PARAM_RANGES: Record<keyof AltSurfaceParams, AltSurfaceParamRange> = {
  windowSize: { min: 3, max: 30, step: 1 },
  hlShort: { min: 3, max: 30, step: 1 },
  windowLong: { min: 3, max: 90, step: 1 },
  hlLong: { min: 30, max: 180, step: 1 },
};

const clampToRange = (key: keyof AltSurfaceParams, value: number): number => {
  const { min, max } = ALT_SURFACE_PARAM_RANGES[key];
  return Math.min(max, Math.max(min, Math.round(value)));
};

// Apply one slider edit to the parameter set, integer-clamped to its range and
// preserving the invariant hlShort < hlLong. When a drag would make the two
// half-lives meet or cross, the *other* half-life is nudged aside rather than
// blocking the drag: raising hlShort to >= hlLong pushes hlLong up to
// hlShort + 1 (capped at its max); lowering hlLong to <= hlShort pulls hlShort
// down to hlLong - 1 (floored at its min). Window size is independent.
export const applyAltSurfaceParam = (
  prev: AltSurfaceParams,
  key: keyof AltSurfaceParams,
  value: number,
): AltSurfaceParams => {
  const next: AltSurfaceParams = { ...prev, [key]: clampToRange(key, value) };

  if (key === 'hlShort' && next.hlShort >= next.hlLong) {
    next.hlLong = clampToRange('hlLong', next.hlShort + 1);
    // hlLong capped at its max and still not clear: give ground on hlShort.
    if (next.hlShort >= next.hlLong) next.hlShort = clampToRange('hlShort', next.hlLong - 1);
  } else if (key === 'hlLong' && next.hlLong <= next.hlShort) {
    next.hlShort = clampToRange('hlShort', next.hlLong - 1);
    // hlShort floored at its min and still not clear: give ground on hlLong.
    if (next.hlLong <= next.hlShort) next.hlLong = clampToRange('hlLong', next.hlShort + 1);
  }

  return next;
};
